import { ArrowRight, Play } from 'lucide-react';
import { buttonVariants } from './ui/Button';
import { cn } from '@/lib/utils';
import { LogPulseLogo } from './LogPulseLogo';
import { SearchDemo } from './product-tour/SearchDemo';
import { useT } from '@/i18n/provider';
import { trackEvent } from '@/lib/analytics';

export function HeroSection() {
  const t = useT();

  return (
    <section className="relative min-h-screen flex items-center gradient-mesh overflow-hidden">
      {/* Decorative elements */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-1/4 -right-20 w-96 h-96 rounded-full bg-primary-500/5 blur-3xl" />
        <div className="absolute bottom-1/4 -left-20 w-80 h-80 rounded-full bg-primary-400/5 blur-3xl" />

        {/* Floating pulse logos */}
        <div className="absolute top-[20%] right-[15%] opacity-[0.07] dark:opacity-[0.05]">
          <LogPulseLogo className="h-40 w-40" />
        </div>
        <div className="absolute bottom-[25%] left-[10%] opacity-[0.05] dark:opacity-[0.03]">
          <LogPulseLogo className="h-24 w-24" />
        </div>
      </div>

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-32 pb-20 sm:pt-40 sm:pb-28">
        <div className="max-w-3xl mx-auto text-center">
          {/* Badge */}
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-primary-50 dark:bg-primary-900/20 border border-primary-200 dark:border-primary-800/30 mb-8 animate-fade-in">
            <div className="w-2 h-2 rounded-full bg-primary-500 animate-pulse" />
            <span className="text-sm font-medium text-primary-700 dark:text-primary-300">
              Now in Early Access
            </span>
          </div>

          {/* Headline */}
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight text-balance animate-slide-up">
            <span className="text-gradient">{t('hero.title')}</span>
          </h1>

          {/* Subtitle */}
          <p className="mt-6 text-lg sm:text-xl text-gray-600 dark:text-gray-400 max-w-2xl mx-auto text-balance animate-slide-up" style={{ animationDelay: '0.1s' }}>
            {t('hero.subtitle')}
          </p>

          {/* CTAs */}
          <div className="mt-10 flex flex-col sm:flex-row items-center justify-center gap-3 sm:gap-4 animate-slide-up" style={{ animationDelay: '0.2s' }}>
            <a
              href="https://app.logpulse.io/sign-in"
              className={buttonVariants({ variant: 'gradient', size: '2xl' })}
              onClick={() => trackEvent('hero_cta_click', { cta: 'primary' })}
            >
              {t('hero.cta')}
              <ArrowRight className="h-5 w-5 ml-1" />
            </a>
            <a
              href="#demo"
              className={cn(buttonVariants({ variant: 'outline', size: '2xl' }))}
              onClick={() => trackEvent('hero_cta_click', { cta: 'secondary' })}
            >
              <Play className="h-4 w-4 mr-1.5" />
              {t('hero.ctaSecondary')}
            </a>
          </div>

          {/* Live search demo */}
          <div className="mt-16 sm:mt-20 animate-slide-up" style={{ animationDelay: '0.3s' }}>
            <div className="relative mx-auto max-w-4xl w-full overflow-hidden rounded-2xl">
              <SearchDemo compact fast />
              {/* Glow effect behind */}
              <div className="absolute -inset-4 -z-10 bg-gradient-to-r from-primary-500/20 via-primary-400/10 to-primary-500/20 rounded-3xl blur-2xl opacity-60" />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
